import { useState, type DragEvent, type KeyboardEvent } from 'react';

type DragHandler = (event: DragEvent<HTMLElement>) => void;

export interface DragReorder {
  /** Dropping splices the carried row in at this index, so coming from ABOVE it
   *  ends up after this row and from below it ends up before; the bar says which. */
  rowState: (id: string, index: number) => 'dragging' | 'overBefore' | 'overAfter' | null;
  /** Handlers for a row as a drop target. */
  targetProps: (id: string) => { onDragOver: DragHandler; onDrop: DragHandler };
  /** Handlers for the element that starts the drag: the row, or a grip on it. */
  sourceProps: (id: string) => {
    draggable: true;
    onDragStart: DragHandler;
    onDragEnd: () => void;
  };
  /** The keyboard route: arrow keys on the grip move the row one place. */
  gripKeyDown: (index: number) => (event: KeyboardEvent<HTMLElement>) => void;
  /** Handlers for the list itself, so a drop past the last row lands at the end. */
  listProps: (accepts?: (event: DragEvent<HTMLElement>) => boolean) => {
    onDragOver: DragHandler;
    onDrop: DragHandler;
  };
}

/**
 * One grammar for sorting by hand: a bar where the carried row would land, the
 * row dimmed, arrow keys for anyone without a pointer. The carried row and the
 * target are held as IDS, not indices: the array is rewritten mid-drag.
 */
export function useDragReorder(
  ids: string[],
  move: (fromId: string, toId: string) => void,
  options: {
    /** What the browser draws instead of the drag source itself. */
    dragImage?: (source: HTMLElement) => HTMLElement | null;
  } = {},
): DragReorder {
  const [dragId, setDragId] = useState<string | null>(null);
  const [overId, setOverId] = useState<string | null>(null);
  const dragIndex = dragId ? ids.indexOf(dragId) : -1;
  const last = ids[ids.length - 1];

  const endDrag = () => {
    setDragId(null);
    setOverId(null);
  };

  return {
    rowState: (id, index) => {
      if (dragId === id) return 'dragging';
      if (overId !== id) return null;
      return dragIndex !== -1 && dragIndex < index ? 'overAfter' : 'overBefore';
    },

    targetProps: (id) => ({
      onDragOver: (event) => {
        if (!dragId) return;
        // The default is "no drop here"; stopped so the list's fallback cannot overwrite it.
        event.preventDefault();
        event.stopPropagation();
        event.dataTransfer.dropEffect = 'move';
        setOverId(id);
      },
      onDrop: (event) => {
        event.preventDefault();
        event.stopPropagation();
        if (dragId) move(dragId, id);
        endDrag();
      },
    }),

    sourceProps: (id) => ({
      draggable: true,
      onDragStart: (event) => {
        setDragId(id);
        // Firefox starts no drag at all without data on the transfer.
        event.dataTransfer.effectAllowed = 'move';
        event.dataTransfer.setData('text/plain', id);
        const image = options.dragImage?.(event.currentTarget);
        if (image) {
          const rect = image.getBoundingClientRect();
          event.dataTransfer.setDragImage(
            image,
            event.clientX - rect.left,
            event.clientY - rect.top,
          );
        }
      },
      onDragEnd: endDrag,
    }),

    gripKeyDown: (index) => (event) => {
      const step = event.key === 'ArrowUp' ? -1 : event.key === 'ArrowDown' ? 1 : 0;
      const id = ids[index];
      const target = ids[index + step];
      if (step === 0 || !id || !target) return;
      event.preventDefault();
      move(id, target);
    },

    listProps: (accepts = () => true) => ({
      onDragOver: (event) => {
        if (!dragId || !accepts(event)) return;
        event.preventDefault();
        event.dataTransfer.dropEffect = 'move';
        setOverId(last ?? null);
      },
      onDrop: (event) => {
        if (!dragId || !accepts(event)) return;
        event.preventDefault();
        if (last) move(dragId, last);
        endDrag();
      },
    }),
  };
}
